import {
  Activity,
  BellRing,
  Blocks,
  BadgeDollarSign,
  BriefcaseBusiness,
  Building2,
  CarFront,
  CarTaxiFront,
  FileClock,
  FileSpreadsheet,
  FileSignature,
  Files,
  HeartPulse,
  House,
  Landmark,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  PlugZap,
  ReceiptText,
  Settings,
  Workflow,
  Users,
  WalletCards,
  Wrench,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { PlatformModule } from "@yatta/tenant-config";
import { useAuth } from "../auth/AuthProvider";
import { useTenantRuntime } from "../config/TenantRuntime";
import { env } from "../lib/env";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  module: PlatformModule;
  requiredModules?: PlatformModule[];
}

const baseNav: NavItem[] = [
  { to: "/", label: "Visão geral", icon: LayoutDashboard, module: "dashboard" },
  { to: "/clientes", label: "Clientes", icon: Users, module: "customers" },
  { to: "/vendas", label: "Vendas", icon: BriefcaseBusiness, module: "sales" },
  { to: "/equipe", label: "Gestão de vendedores", icon: Users, module: "sales" },
  { to: "/produtos", label: "Produtos e ofertas", icon: Package, module: "catalog" },
  { to: "/contratos", label: "Contratos", icon: FileSignature, module: "contracts" },
  { to: "/financeiro", label: "Financeiro", icon: ReceiptText, module: "billing" },
  { to: "/comissoes", label: "Comissões", icon: BadgeDollarSign, module: "commissions" },
  { to: "/telemedicina", label: "Telemedicina", icon: HeartPulse, module: "telemedicine" },
  { to: "/assistencia", label: "Assistência", icon: Activity, module: "funeral_assistance" },
  {
    to: "/assistencia-funeraria",
    label: "Planos funerários",
    icon: Activity,
    module: "funeral_assistance",
  },
  { to: "/patrimonio", label: "Patrimônio", icon: Landmark, module: "assets" },
  { to: "/vendas-veiculos", label: "Venda de veículos", icon: CarFront, module: "vehicle_sales" },
  {
    to: "/locacoes-imoveis",
    label: "Locação de imóveis",
    icon: Building2,
    module: "property_leases",
  },
  {
    to: "/locacoes-veiculos",
    label: "Locação de veículos",
    icon: CarTaxiFront,
    module: "vehicle_rentals",
  },
  { to: "/despesas-ativos", label: "Despesas dos ativos", icon: Wrench, module: "asset_expenses" },
  {
    to: "/contratos-software",
    label: "Contratos de software",
    icon: FileSignature,
    module: "software_contracts",
  },
  {
    to: "/recebiveis",
    label: "Recebíveis",
    icon: FileClock,
    module: "receivable_schedules",
  },
  { to: "/workflows", label: "Workflows", icon: Workflow, module: "workflow_engine" },
  { to: "/notificacoes", label: "Notificações", icon: BellRing, module: "notifications" },
  {
    to: "/modelos-documentos",
    label: "Modelos de documentos",
    icon: Files,
    module: "document_builder",
  },
  { to: "/importacoes", label: "Importações", icon: FileSpreadsheet, module: "universal_importer" },
  { to: "/fabrica-produto", label: "Fábrica do produto", icon: Blocks, module: "custom_fields" },
  { to: "/integracoes", label: "Integrações", icon: PlugZap, module: "integrations" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, module: "settings" },
];

const titles: Record<string, string> = {
  "/": "Visão geral",
  "/clientes": "Clientes",
  "/vendas": "Vendas",
  "/equipe": "Gestão de vendedores",
  "/produtos": "Produtos e ofertas",
  "/contratos": "Contratos",
  "/financeiro": "Financeiro",
  "/comissoes": "Comissões",
  "/telemedicina": "Telemedicina",
  "/assistencia": "Assistência funerária",
  "/assistencia-funeraria": "Planos funerários",
  "/patrimonio": "Patrimônio",
  "/vendas-veiculos": "Venda de veículos",
  "/locacoes-imoveis": "Locação de imóveis",
  "/locacoes-veiculos": "Locação de veículos",
  "/despesas-ativos": "Despesas dos ativos",
  "/recebiveis": "Recebíveis",
  "/contratos-software": "Contratos de software",
  "/workflows": "Workflows",
  "/notificacoes": "Notificações",
  "/modelos-documentos": "Modelos de documentos",
  "/importacoes": "Importações",
  "/fabrica-produto": "Fábrica do produto",
  "/integracoes": "Integrações",
  "/configuracoes": "Configurações",
  "/yatta": "Clientes da plataforma",
  "/yatta/suporte": "Console de suporte",
  "/yatta/fabrica": "Fábrica de tenants",
  "/yatta/faturamento": "Faturamento SaaS",
};

export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { tenant, hasModule } = useTenantRuntime();
  const { displayName, isPlatformMember, signOut } = useAuth();
  const isFlow = tenant.product.commercialProductCode === "EVA_FLOW";
  const assetVertical = hasModule("assets") && !isFlow;
  const title =
    (isFlow && location.pathname === "/assistencia"
      ? "Módulos da EVA Flow"
      : titles[location.pathname]) ??
    (location.pathname.startsWith("/clientes/")
      ? assetVertical
        ? "Detalhes do cliente"
        : "Detalhes da família"
      : tenant.branding.shortName);
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  const flowFirst = ["/", "/contratos", "/recebiveis", "/financeiro", "/assistencia", "/produtos"];
  const nav: NavItem[] = baseNav
    .filter((item) =>
      isFlow
        ? !["/patrimonio", "/despesas-ativos"].includes(item.to)
        : item.to !== "/assistencia-funeraria" && item.to !== "/contratos-software",
    )
    .map((item): NavItem => {
      if (item.to === "/clientes")
        return {
          ...item,
          label: isFlow || assetVertical ? "Clientes e empresas" : "Clientes e famílias",
        };
      if (isFlow && item.to === "/assistencia")
        return { ...item, label: "Módulos da Flow", module: "catalog" };
      return item;
    })
    .sort((a, b) =>
      isFlow
        ? (flowFirst.indexOf(a.to) < 0 ? 99 : flowFirst.indexOf(a.to)) -
          (flowFirst.indexOf(b.to) < 0 ? 99 : flowFirst.indexOf(b.to))
        : 0,
    );

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        <div className="brand">
          <div className="brand-mark">
            <House size={22} />
          </div>
          <div>
            <strong>{tenant.branding.shortName}</strong>
            <span>Uma solução Yatta Tecnologia</span>
          </div>
          <button
            className="sidebar-close"
            type="button"
            aria-label="Fechar menu"
            onClick={() => setOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Navegação principal">
          {nav
            .filter(
              (item) =>
                hasModule(item.module) &&
                (item.requiredModules ?? []).every((module) => hasModule(module)),
            )
            .map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  end={item.to === "/"}
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          {isPlatformMember && (
            <>
              <NavLink
                to="/yatta"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <Building2 size={18} />
                <span>Plataforma Yatta</span>
              </NavLink>
              <NavLink
                to="/yatta/fabrica"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <Blocks size={18} />
                <span>Fábrica de tenants</span>
              </NavLink>
              <NavLink
                to="/yatta/suporte"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <Activity size={18} />
                <span>Suporte Yatta</span>
              </NavLink>
              <NavLink
                to="/yatta/faturamento"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <BadgeDollarSign size={18} />
                <span>Faturamento SaaS</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <span className="environment-pill">
            {env.useMocks ? "DEMONSTRAÇÃO" : tenant.product.releaseChannel.toUpperCase()}
          </span>
          <small>
            {env.useMocks
              ? "Dados fictícios para apresentação"
              : `${tenant.slug} · ${tenant.product.planCode}`}
          </small>
        </div>
      </aside>

      {open && (
        <button
          className="sidebar-backdrop"
          aria-label="Fechar menu"
          onClick={() => setOpen(false)}
        />
      )}

      <main className="main-area">
        <header className="topbar">
          <button className="menu-button" type="button" onClick={() => setOpen(true)}>
            <Menu size={21} />
          </button>
          <div>
            <p className="eyebrow">{tenant.branding.shortName.toUpperCase()} · YATTA TECNOLOGIA</p>
            <h1>{title}</h1>
          </div>
          <div className="topbar-actions">
            {hasModule("public_onboarding") && (
              <NavLink className="portal-link" to="/onboarding">
                Novo autocadastro
              </NavLink>
            )}
            {hasModule("public_payment_portal") && (
              <NavLink className="portal-link" to="/segunda-via">
                <WalletCards size={15} /> Segunda via
              </NavLink>
            )}
            {env.useMocks ? (
              <span
                className="demo-context"
                aria-label="Ambiente de demonstração segura com dados fictícios"
              >
                Demonstração segura
              </span>
            ) : (
              <>
                <button
                  className="user-avatar"
                  type="button"
                  title={`${displayName} — sair`}
                  onClick={() => void signOut()}
                >
                  {initials || "YT"}
                </button>
                <LogOut className="logout-icon" size={16} aria-hidden="true" />
              </>
            )}
          </div>
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}
